import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// import { MdOutlineKeyboardArrowDown } from 'react-icons/md';
import { use100vh } from 'react-div-100vh';
import { useLocation } from 'react-router-dom';
import VirtualList from 'react-tiny-virtual-list';
import { useUpdateEffect } from 'react-use';
import AutoSizer from 'react-virtualized-auto-sizer';
import removeAccents from 'remove-accents';

import { NotFoundResult } from '../NotFoundResult';

import { ButtonsTagsPositionsF11 } from './ButtonsTagsPositionsF11';
import { ButtonsTagsPositionsF5 } from './ButtonsTagsPositionsF5';

import { InputSearch, PlayerList, Tags } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { useNormalizeInitialF5 } from '@/hooks/useNormalizeInitialF5';
import { useGetPlayerPositionByAbbreviation } from '@/hooks/usePlayer';
import { useTeamStore } from '@/hooks/useTeamStore';
import { useTournament } from '@/hooks/useTournament';
import ROUTES from '@/router/routes';
import { TeamActionTypes } from '@/store/team';
import { KeysOf } from '@/types';
import { EnumTeamContext, TemplatePlayer } from '@/types/team';
import { areArrayEqual, formatMillionBalance } from '@/utils';
import {
  MAX_INPUT_RANGE,
  MIN_INPUT_RANGE,
  ORDER_FILTER_LIST,
  PLAYER_POSITION_BY_ID,
  POSITIONS_LABELS,
} from '@/utils/constants';

type PositionKeys = KeysOf<typeof PLAYER_POSITION_BY_ID>;

type PlayerPickerProps = {
  handleSelectPlayerOnList: (player: TemplatePlayer) => void;
  context?: EnumTeamContext;
};

type Terms = {
  directionTeam: boolean;
  directionAverage: boolean;
  searchPlayerValue: string;
  playersByFilter: TemplatePlayer[];
};

export const PlayerPicker = ({
  handleSelectPlayerOnList,
  context,
}: PlayerPickerProps) => {
  const { userInitialData } = useAuth();
  const { tournamentMap } = useTournament();
  const { isDesktop, isMobile } = useIsDesktop();
  const { normalizeInitialF5 } = useNormalizeInitialF5();

  const initialValueStatePicker = {
    directionTeam: false,
    directionAverage: false,
    searchPlayerValue: '',
    playersByFilter:
      userInitialData?.plantilla.jugadores.filter(
        (player) => !player.eliminado
      ) || [],
  };

  const [statePicker, setStatePicker] = useState<Terms>(
    initialValueStatePicker
  );
  const { state, dispatch } = useTeamStore();

  const location = useLocation();
  const isBuildF5 = location.pathname.includes(ROUTES.buildTeam5);

  const realHeight = use100vh();

  const vlistRef = useRef<VirtualList>(null); // RefObject<VirtualList>
  const filterItemsRef = useRef<HTMLDivElement>(null);

  // const { playersOnField } = state;
  const tournamentStatusMap = tournamentMap?.tournamentStatusMap;
  const teamsMap = tournamentMap?.tournamentTeamsMap;

  const isF5 =
    context &&
    [
      EnumTeamContext.FilterTeam5,
      EnumTeamContext.Build5,
      EnumTeamContext.Team5,
    ].includes(context);

  const ButtonsTagsPositions = isBuildF5
    ? ButtonsTagsPositionsF5
    : ButtonsTagsPositionsF11;

  const selectedPositionLabel = useGetPlayerPositionByAbbreviation(
    state.selectedPosition
  );

  const resetSearch = () => {
    setStatePicker({
      ...statePicker,
      searchPlayerValue: '',
    });
  };

  useUpdateEffect(() => {
    dispatch({
      type: TeamActionTypes.SET_STATE,
      payload: {
        filtersApplied: {
          ...state.filtersApplied,
          position: null,
        },
      },
    });
  }, [state.selectedPosition]);

  const getFilteredPlayerListBySearch = (
    playerList: TemplatePlayer[],
    searchTerm: string
  ) => {
    return playerList.filter((player) => {
      const querySearch = searchTerm.toLowerCase();
      const lastName = removeAccents(player.a).toLowerCase();
      return lastName.startsWith(querySearch);
    });
  };

  const getPlayersByOrder = useCallback(
    (list: TemplatePlayer[]) => {
      if (state.filtersApplied?.orderBy) {
        if (state.filtersApplied?.orderBy.value === 1) {
          return list.sort(
            (listA: TemplatePlayer, listB: TemplatePlayer): any => {
              return listA.a < listB.a;
            }
          );
        } else if (state.filtersApplied?.orderBy.value === 2) {
          return list.sort(function (
            listA: TemplatePlayer,
            listB: TemplatePlayer
          ): number {
            return isF5
              ? Number(listB.dtsF5) - Number(listA.dtsF5)
              : listB.dts - listA.dts;
          });
        } else if (state.filtersApplied?.orderBy.value === 3) {
          return list.sort(function (
            listA: TemplatePlayer,
            listB: TemplatePlayer
          ): number {
            if (isF5) {
              return listA.dtsF5 - listB.dtsF5;
            } else {
              return listA.dts - listB.dts;
            }
          });
        } else if (state.filtersApplied?.orderBy.value === 4) {
          return list.sort(function (
            listA: TemplatePlayer,
            listB: TemplatePlayer
          ): number {
            return (
              parseFloat(listB.pm.replace(',', '.')) -
              parseFloat(listA.pm.replace(',', '.'))
            );
          });
        } else if (state.filtersApplied?.orderBy.value === 5) {
          return list.sort(function (
            listA: TemplatePlayer,
            listB: TemplatePlayer
          ): number {
            return (
              parseFloat(listA.pm.replace(',', '.')) -
              parseFloat(listB.pm.replace(',', '.'))
            );
          });
        } else if (state.filtersApplied?.orderBy.value === 6) {
          return list.sort(function (
            listA: TemplatePlayer,
            listB: TemplatePlayer
          ): number {
            return listB.cot - listA.cot;
          });
        } else if (state.filtersApplied?.orderBy.value === 7) {
          return list.sort(function (
            listA: TemplatePlayer,
            listB: TemplatePlayer
          ): number {
            return listA.cot - listB.cot;
          });
        }
      }
    },
    [isF5, state.filtersApplied?.orderBy]
  );

  const getPlayersByClub = useCallback(
    (list: TemplatePlayer[]) => {
      if (
        (state.filtersApplied?.orderClub?.value &&
          Math.sign(state.filtersApplied?.orderClub?.value) !== -1) ||
        state.filtersApplied?.orderClub?.value === 0
      ) {
        return list.filter((player: TemplatePlayer) => {
          return player.c === state.filtersApplied?.orderClub?.value;
        });
      }
    },
    [state.filtersApplied?.orderClub?.value]
  );

  const getPlayersByRange = useCallback(
    (list: TemplatePlayer[]) => {
      if (state.filtersApplied?.valuesRange) {
        const value1 = state.filtersApplied?.valuesRange[0] || 0;
        const value2 = state.filtersApplied?.valuesRange[1] || 0;
        if (state.filtersApplied?.valuesRange) {
          return list.filter((player: TemplatePlayer) => {
            return player.cot * 10000 >= value1 && player.cot * 10000 <= value2;
          });
        }
      }
    },
    [state.filtersApplied?.valuesRange]
  );

  const getPlayerIntoBudget = useCallback(
    (list: TemplatePlayer[]) => {
      if (state.filtersApplied?.intoBudget) {
        // eslint-disable-next-line array-callback-return
        return list.filter((player: TemplatePlayer) => {
          if (state?.balance && state?.balance >= 300000) {
            return player.cot * 10000 <= state?.balance;
          }
        });
      }
    },
    [state?.balance, state.filtersApplied?.intoBudget]
  );

  const applyFilters = useCallback(
    (list: TemplatePlayer[], bySearch: boolean) => {
      list = bySearch
        ? getFilteredPlayerListBySearch(list, statePicker.searchPlayerValue)
        : list;
      list = (state.filtersApplied?.orderBy && getPlayersByOrder(list)) || list;
      list =
        (state.filtersApplied?.valuesRange && getPlayersByRange(list)) || list;
      list =
        (state.filtersApplied?.intoBudget && getPlayerIntoBudget(list)) || list;
      list =
        (state.filtersApplied?.orderClub && getPlayersByClub(list)) || list;
      return list;
    },
    [
      getPlayerIntoBudget,
      getPlayersByClub,
      getPlayersByOrder,
      getPlayersByRange,
      state.filtersApplied?.intoBudget,
      state.filtersApplied?.orderBy,
      state.filtersApplied?.orderClub,
      state.filtersApplied?.valuesRange,
      statePicker.searchPlayerValue,
    ]
  );

  const handleRemoveFilters = useCallback(
    (filter: number) => {
      // eslint-disable-next-line array-callback-return
      const filterKeyMap: { [key in 1 | 2 | 3 | 4]: string } = {
        1: 'intoBudget',
        2: 'orderBy',
        3: 'orderClub',
        4: 'valuesRange',
      };

      const filterKey = filterKeyMap[filter as 1 | 2 | 3 | 4];

      if (filterKey) {
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            filtersApplied: {
              ...state.filtersApplied,
              [filterKey]: filterKey === 'intoBudget' ? false : null,
            },
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.filtersApplied, state.selectedPosition]
  );

  const filtersAppliedList = useMemo(() => {
    if (!state?.filtersApplied) return [];

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { positionF5, ...filters } = state.filtersApplied;
    const result = [];

    if (filters.intoBudget) {
      result.push({
        label: 'Dentro del presupuesto',
        value: filters.intoBudget,
        filterId: 1,
      });
    }

    if (filters.orderBy) {
      result.push({
        label: filters.orderBy.label,
        value: filters.orderBy,
        filterId: 2,
      });
    }

    if (filters.orderClub) {
      result.push({
        label: filters.orderClub.label,
        value: filters.orderClub,
        filterId: 3,
      });
    }

    if (
      filters.valuesRange &&
      !areArrayEqual(filters.valuesRange, [MIN_INPUT_RANGE, MAX_INPUT_RANGE])
    ) {
      result.push({
        label: `Entre ${formatMillionBalance(
          filters.valuesRange[0]
        )} y ${formatMillionBalance(filters.valuesRange[1])}`,
        value: filters.valuesRange,
        filterId: 4,
      });
    }

    return (
      result?.length > 0 &&
      result.map((filter, index) => {
        if (filter)
          return (
            <div key={index} className="lg:mt-2 lg:!mr-[5px] lg:inline-block">
              <Tags.Action onClick={() => handleRemoveFilters(filter.filterId)}>
                {filter.label}
              </Tags.Action>
            </div>
          );
        return null;
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.filtersApplied]);

  const filteredPlayers = useMemo(() => {
    if (statePicker.playersByFilter) {
      // clear orderby direction *** se quita en // https://agea.atlassian.net/browse/DTA-6321
      // setStatePicker({
      //   ...statePicker,
      //   directionAverage: false,
      //   directionTeam: false,
      // });

      // Si hay una posicion seleccionada, filtrar por esa posicion
      if (state.selectedPosition || (isF5 && state.selectedPlayerOnBuildTeam)) {
        let filteredList: TemplatePlayer[] = [];
        if (
          isF5 &&
          state.selectedPlayerOnBuildTeam &&
          state.selectedPosition !== POSITIONS_LABELS.abbreviation.ARQ
        ) {
          if (state.filtersApplied?.positionF5?.length === 0) {
            return [];
          }
          filteredList = statePicker.playersByFilter.filter((player) => {
            const playerPosition =
              PLAYER_POSITION_BY_ID[player.p as PositionKeys];

            if (state.filtersApplied?.positionF5) {
              return state.filtersApplied?.positionF5?.includes(playerPosition);
            } else {
              return playerPosition !== POSITIONS_LABELS.abbreviation.ARQ;
            }
          });
        } else {
          filteredList = statePicker.playersByFilter.filter((player) => {
            const playerPosition =
              PLAYER_POSITION_BY_ID[player.p as PositionKeys];
            return playerPosition === state.selectedPosition;
          });
        }

        if (filteredList.length > 0) {
          if (statePicker.searchPlayerValue !== '') {
            return applyFilters(filteredList, true);
          } else {
            return applyFilters(filteredList, false);
          }
        }
      } else {
        return applyFilters(statePicker.playersByFilter, true);
      }
    }

    return [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isF5,
    statePicker.playersByFilter,
    statePicker.searchPlayerValue,
    state.playersOnField,
    state.selectedPosition,
    state.filtersApplied,
  ]);

  // uso la lista ya limitada por filtros y la ordeno dependiendo de los equipo y los promedios
  const orderByAscDesc = useCallback(
    (orderBy: string) => {
      if (orderBy === 'TEAM') {
        setStatePicker({
          ...statePicker,
          directionAverage: false,
          directionTeam: !statePicker.directionTeam,
        });

        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            filtersApplied: {
              ...state.filtersApplied,
              orderBy: !statePicker.directionTeam
                ? ORDER_FILTER_LIST.find((selected) => selected.value === 2)
                : ORDER_FILTER_LIST.find((selected) => selected.value === 3),
            },
          },
        });

        return [
          ...filteredPlayers.sort(function (
            a: TemplatePlayer,
            b: TemplatePlayer
          ) {
            if (isF5) {
              return statePicker.directionTeam
                ? a.dtsF5 - b.dtsF5
                : b.dtsF5 - a.dtsF5;
            } else {
              return statePicker.directionTeam ? a.dts - b.dts : b.dts - a.dts;
            }
          }),
        ];
      } else {
        setStatePicker({
          ...statePicker,
          directionTeam: false,
          directionAverage: !statePicker.directionAverage,
        });
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            filtersApplied: {
              ...state.filtersApplied,
              orderBy: !statePicker.directionAverage
                ? ORDER_FILTER_LIST.find((selected) => selected.value === 4)
                : ORDER_FILTER_LIST.find((selected) => selected.value === 5),
            },
          },
        });
        return [
          ...filteredPlayers.sort(function (
            a: TemplatePlayer,
            b: TemplatePlayer
          ) {
            const firstValue: number = parseFloat(a.pm.replace(',', '.'));
            const secondValue: number = parseFloat(b.pm.replace(',', '.'));

            if (statePicker.directionAverage) {
              return firstValue - secondValue;
            } else {
              return secondValue - firstValue;
            }
          }),
        ];
      }
    },
    [dispatch, filteredPlayers, isF5, state.filtersApplied, statePicker]
  );

  // seteo el orden segun ventana de veda (siempre "en mas equipos" desc, sin toggle)
  useEffect(() => {
    if (state.isBeforeVeda && !state.filtersApplied?.orderBy) {
      const timeout = setTimeout(() => {
        setStatePicker((prevStatePicker) => ({
          ...prevStatePicker,
          directionTeam: true,
          directionAverage: false,
        }));
        dispatch({
          type: TeamActionTypes.SET_STATE,
          payload: {
            filtersApplied: {
              ...state.filtersApplied,
              orderBy: ORDER_FILTER_LIST.find(
                (selected) => selected.value === 2
              ),
            },
          },
        });
      }, 300);

      return () => clearTimeout(timeout);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.isBeforeVeda, state.filtersApplied?.orderBy]);

  useEffect(() => {
    if (!vlistRef.current) return;
    vlistRef.current.scrollTo(0);
  }, [filteredPlayers]);

  const [heightFilterApplied, setHeightFilterApplied] = useState(0);

  useEffect(() => {
    if (filterItemsRef?.current) {
      const rect = filterItemsRef.current.getBoundingClientRect();
      setHeightFilterApplied(rect.height);
    } else {
      setHeightFilterApplied(0);
    }
  }, [filterItemsRef, filtersAppliedList]);

  // lleno estado global normalizado para filtros
  useEffect(() => {
    if (isBuildF5) {
      dispatch({
        type: TeamActionTypes.SET_STATE,
        payload: {
          filtersApplied: {
            ...state.filtersApplied,
            positionF5: normalizeInitialF5,
          },
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.selectedPosition]);

  return (
    <>
      <div className="space-y-4 px-5 py-4">
        {isDesktop && (
          <h4 className="text-gdt-gray text-base font-semibold">
            {state.selectedPlayerOnTeam && 'a' in state.selectedPlayerOnTeam
              ? `Transferir a ${state.selectedPlayerOnTeam.a}`
              : 'Seleccioná tus jugadores'}
          </h4>
        )}
        <InputSearch
          handleCancel={() => resetSearch()}
          name="search-player"
          placeholder="Buscar por apellido"
          value={statePicker.searchPlayerValue}
          onChange={(event) => {
            const containNumbers = /\d/.test(event.target.value);

            if (containNumbers) {
              return;
            }

            setStatePicker({
              ...statePicker,
              searchPlayerValue: removeAccents(event.target.value),
            });
          }}
        />
        {/* applied filters */}
        {filtersAppliedList &&
          (Object.values(filtersAppliedList as any).some(
            (filter) => filter !== undefined
          ) ? (
            <div
              ref={filterItemsRef}
              className="lg:w-[326px] lg:block flex overflow-x-auto items-center justify-start lg:whitespace-normal whitespace-nowrap lg:space-x-0 lg:!mt-2 lg:pb-0 space-x-3 pb-2"
            >
              {filtersAppliedList}
            </div>
          ) : null)}
        {/* / positions buttons */}
        <ButtonsTagsPositions />
      </div>
      <div className="flex justify-between items-center pl-[20px] pr-[23px] py-[16px] lg:pr-[28px] lg:pt-0 pt-1">
        <div>
          <p className="text-sm lg:text-xs text-gdt-gray-2 font-semibold">
            Lista <span className="lowercase">de</span> {selectedPositionLabel}
          </p>
        </div>
        <div className="flex max-w-[100px] w-full">
          <div
            className="flex-1 cursor-pointer"
            onClick={() => orderByAscDesc('TEAM')}
          >
            <p className="text-3xs text-gdt-gray-4 font-medium text-center">
              <span className="flex justify-center items-center" role="button">
                Equipos <br /> GDT
                {/* <MdOutlineKeyboardArrowDown
                  // dejo comentado por si mkt se le antoja poner nuevamente flechas
                  className={cn({
                    'rotate-180': !orderDirection.directionTeam,
                  })}
                /> */}
              </span>
            </p>
          </div>
          <div
            className="flex-1 cursor-pointer"
            onClick={() => orderByAscDesc('AVERAGE')}
          >
            <p className="text-3xs text-gdt-gray-4 font-medium text-center">
              <span className="flex justify-center items-center" role="button">
                {isDesktop ? 'Puntos' : 'Ptos.'} <br /> Prom.
                {/* <MdOutlineKeyboardArrowDown
                  // dejo comentado por si mkt se le antoja poner nuevamente flechas
                  className={cn({
                    'rotate-180': !orderDirection.directionAverage,
                  })}
                /> */}
              </span>
            </p>
          </div>
        </div>
      </div>
      <div className="space-y-1 h-screen lg:h-full">
        {filteredPlayers.length > 0 ? (
          <AutoSizer className="flex-1 pb-2">
            {({ height, width }) => {
              const FILTER_HEADER_HEIGHT = 275;
              const FOOTER_HEIGHT = 177;
              let _height =
                context &&
                [EnumTeamContext.Build11, EnumTeamContext.Build5].includes(
                  context
                )
                  ? height - FOOTER_HEIGHT - heightFilterApplied
                  : (realHeight || 0) -
                    FILTER_HEADER_HEIGHT -
                    heightFilterApplied;

              if (isMobile) {
                _height -= 30;
              }
              return (
                <VirtualList
                  ref={vlistRef}
                  height={_height}
                  itemCount={filteredPlayers.length}
                  itemSize={91}
                  renderItem={({ index, style }) => {
                    const player = filteredPlayers[index];

                    // const isDisabled = selectedPlayerListId.some(
                    //   (id) => id === player.id
                    // );

                    const statusInTournament = tournamentStatusMap
                      ? tournamentStatusMap.get(player.e)
                      : undefined;
                    const team = teamsMap ? teamsMap.get(player.c) : undefined;

                    return (
                      <div key={index} style={style}>
                        <PlayerList.Player
                          className="border-b border-gdt-gray-7"
                          context={context}
                          initialCheckState={false}
                          isSubstitute={false}
                          player={player}
                          team={team?.nombre ?? ''}
                          teamId={team?.id ?? 0}
                          tournamentStatus={statusInTournament}
                          variant="default"
                          onClick={() => handleSelectPlayerOnList(player)}
                        />
                      </div>
                    );
                  }}
                  width={width}
                />
              );
            }}
          </AutoSizer>
        ) : (
          <NotFoundResult />
        )}
      </div>
    </>
  );
};
